import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { MessageService, ConfirmationService } from 'primeng/api';
import { NachaService } from '@app/_components/owner-stmt/nacha/nacha.service';

interface TaxRow {
  ownerStmtId: number;
  propertyCode: string | null;
  ownerName: string | null;
  rentalTaxIdNumber: string | null;
  addressCounty: string | null;
  getAmount: number | null;
  tatAmount: number | null;
  mctatAmount: number | null;
  getNachaBatchId: number | null;
  tatNachaBatchId: number | null;
  mctatNachaBatchId: number | null;
  // UI state
  selected?: boolean;
}

interface DistRow {
  ownerStmtId: number;
  propertyCode: string | null;
  ownerName: string | null;
  bankRoutingNum: string | null;
  bankAccountNum: string | null;
  bankName: string | null;
  distributionAmount: number | null;
  nachaDistBatchId: number | null;
  // UI state
  selected?: boolean;
}

@Component({
  selector: 'app-nacha-tools-dialog',
  templateUrl: './nacha-tools-dialog.component.html',
  styleUrls: ['./nacha-tools-dialog.component.scss'],
  providers: [ConfirmationService],
  standalone: false
})
export class NachaToolsDialogComponent implements OnChanges {

  @Input() visible = false;
  @Input() month!: number;
  @Input() year!: number;
  @Output() visibleChange = new EventEmitter<boolean>();

  activeTab: 'tax' | 'dist' = 'tax';

  /** Toggled by the (i) help button on the Owner Distribution tab — shows the
   *  legend explaining row colors, the meaning of "missing", and why a row
   *  may not be selectable. Defaults to false so the dialog stays compact. */
  showDistHelp = false;

  loading = false;
  error: string | null = null;

  taxRows: TaxRow[] = [];
  taxType: 'GET' | 'TAT' | 'MCTAT' = 'GET';

  distRows: DistRow[] = [];
  /** YYMMDD effective date string for the BoH file. */
  effectiveDate = '';
  /** Date object backing the calendar input. Converted to YYMMDD on submit. */
  effectiveDateObj: Date | null = null;

  taxBatches: any[] = [];
  distBatches: any[] = [];

  generating = false;

  constructor(
    private nachaService: NachaService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['visible'] && c['visible'].currentValue === true) {
      this.error = null;
      this.activeTab = 'tax';
      // Default effective date to the 12th of the period month
      // (BoH expects the day funds settle; KBM standard is the 12th)
      this.effectiveDateObj = new Date(this.year, this.month - 1, 12);
      this.effectiveDate = this.toYYMMDD(this.effectiveDateObj);
      this.loadTaxPreview();
      this.loadBatches();
    }
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  switchTab(tab: 'tax' | 'dist') {
    this.activeTab = tab;
    this.error = null;
    if (tab === 'tax' && this.taxRows.length === 0) this.loadTaxPreview();
    if (tab === 'dist' && this.distRows.length === 0) this.loadDistPreview();
  }

  // -----------------------------------------------------------------------
  // TAX TAB
  // -----------------------------------------------------------------------

  loadTaxPreview() {
    this.loading = true;
    this.error = null;
    this.nachaService.getTaxPreviewByPeriod(this.month, this.year).subscribe({
      next: (rows: any[]) => {
        this.taxRows = (rows || []).map(r => ({ ...r, selected: false }));
        // Default-select rows where the chosen tax type is unbatched and > 0
        this.applyTaxDefaultSelection();
        this.loading = false;
      },
      error: (err) => {
        const status = err?.status;
        if (status === 401 || status === 403) {
          this.error = 'Session expired or insufficient permissions. Please log out and log back in.';
        } else {
          this.error = err?.error?.message || err?.message || 'Failed to load tax preview.';
        }
        this.loading = false;
      }
    });
  }

  onTaxTypeChange() {
    this.applyTaxDefaultSelection();
  }

  applyTaxDefaultSelection() {
    for (const r of this.taxRows) {
      const amt = this.taxAmount(r);
      const batched = this.taxBatched(r);
      r.selected = !!(amt && amt > 0 && !batched);
    }
  }

  taxAmount(r: TaxRow): number | null {
    if (this.taxType === 'GET')   return r.getAmount;
    if (this.taxType === 'TAT')   return r.tatAmount;
    if (this.taxType === 'MCTAT') return r.mctatAmount;
    return null;
  }

  taxBatched(r: TaxRow): number | null {
    if (this.taxType === 'GET')   return r.getNachaBatchId;
    if (this.taxType === 'TAT')   return r.tatNachaBatchId;
    if (this.taxType === 'MCTAT') return r.mctatNachaBatchId;
    return null;
  }

  selectAllTax(checked: boolean) {
    for (const r of this.taxRows) {
      const amt = this.taxAmount(r);
      const batched = this.taxBatched(r);
      // Only select rows that have a non-zero amount and aren't already batched
      r.selected = checked && !!amt && amt > 0 && !batched;
    }
  }

  taxSelectedCount(): number {
    return this.taxRows.filter(r => r.selected).length;
  }

  taxSelectedTotal(): number {
    return this.taxRows
      .filter(r => r.selected)
      .reduce((sum, r) => sum + (this.taxAmount(r) || 0), 0);
  }

  exportTaxToExcel() {
    const headers = ['Property', 'Owner', 'Tax ID', 'County', 'GET', 'TAT', 'MCTAT'];
    const rows = this.taxRows.map(r => [
      r.propertyCode || '',
      r.ownerName || '',
      r.rentalTaxIdNumber || '',
      r.addressCounty || '',
      r.getAmount != null ? r.getAmount.toFixed(2) : '0.00',
      r.tatAmount != null ? r.tatAmount.toFixed(2) : '0.00',
      r.mctatAmount != null ? r.mctatAmount.toFixed(2) : '0.00'
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `KBM_Tax_${this.year}-${this.pad2(this.month)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  generateTaxFile() {
    const ids = this.taxRows.filter(r => r.selected).map(r => r.ownerStmtId);
    if (ids.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing selected',
        detail: 'Pick at least one statement.'
      });
      return;
    }
    this.confirmationService.confirm({
      header: 'Generate ' + this.taxType + ' NACHA File?',
      message:
        `Build ${this.taxType} NACHA file for ${ids.length} statement${ids.length === 1 ? '' : 's'} ` +
        `(${this.formatMoney(this.taxSelectedTotal())}). This creates a batch record — ` +
        `selected statements will be marked as batched and won't appear here again. Continue?`,
      acceptLabel: 'Generate File',
      rejectLabel: 'Cancel',
      accept: () => {
        this.generating = true;
        this.nachaService.generateNacha(ids, this.taxType).subscribe({
          next: (resp: HttpResponse<Blob>) => {
            this.generating = false;
            this.downloadBlob(resp, `KBM_${this.taxType}_${this.year}-${this.pad2(this.month)}.ach`);
            this.messageService.add({
              severity: 'success',
              summary: 'NACHA File Generated',
              detail: `Downloaded ${this.taxType} file with ${ids.length} entries. Batch saved.`
            });
            this.loadTaxPreview();
            this.loadBatches();
          },
          error: (err) => {
            this.generating = false;
            this.error = err?.error?.message || err?.message || 'Generation failed.';
            this.messageService.add({
              severity: 'error',
              summary: 'Generation Failed',
              detail: this.error || 'See console for details.'
            });
          }
        });
      }
    });
  }

  // -----------------------------------------------------------------------
  // DISTRIBUTION TAB
  // -----------------------------------------------------------------------

  loadDistPreview() {
    this.loading = true;
    this.error = null;
    this.nachaService.getDistPreviewByPeriod(this.month, this.year).subscribe({
      next: (rows: any[]) => {
        this.distRows = (rows || []).map(r => ({ ...r, selected: false }));
        this.applyDistDefaultSelection();
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || err?.message || 'Failed to load distribution preview.';
        this.loading = false;
      }
    });
  }

  applyDistDefaultSelection() {
    for (const r of this.distRows) {
      r.selected = !!(
        r.distributionAmount && r.distributionAmount > 0 &&
        !r.nachaDistBatchId &&
        this.hasBankInfo(r)
      );
    }
  }

  hasBankInfo(r: DistRow): boolean {
    return !!(r.bankRoutingNum && r.bankAccountNum && r.bankRoutingNum.length === 9);
  }

  selectAllDist(checked: boolean) {
    for (const r of this.distRows) {
      r.selected = checked && !!(
        r.distributionAmount && r.distributionAmount > 0 &&
        !r.nachaDistBatchId &&
        this.hasBankInfo(r)
      );
    }
  }

  distSelectedCount(): number {
    return this.distRows.filter(r => r.selected).length;
  }

  distSelectedTotal(): number {
    return this.distRows
      .filter(r => r.selected)
      .reduce((sum, r) => sum + (r.distributionAmount || 0), 0);
  }

  onEffectiveDateChange() {
    if (this.effectiveDateObj) {
      this.effectiveDate = this.toYYMMDD(this.effectiveDateObj);
    }
  }

  generateDistFile() {
    const ids = this.distRows.filter(r => r.selected).map(r => r.ownerStmtId);
    if (ids.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing selected',
        detail: 'Pick at least one statement.'
      });
      return;
    }
    if (!this.effectiveDate || this.effectiveDate.length !== 6) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Effective Date Required',
        detail: 'Pick the date funds should settle (Bank of Hawaii effective date).'
      });
      return;
    }
    this.confirmationService.confirm({
      header: 'Generate Owner Distribution NACHA File?',
      message:
        `Build BoH ePay file for ${ids.length} owner${ids.length === 1 ? '' : 's'} ` +
        `(${this.formatMoney(this.distSelectedTotal())}), effective ` +
        `${this.effectiveDateObj ? this.effectiveDateObj.toLocaleDateString() : this.effectiveDate}. ` +
        `Selected statements will be marked QUEUED and won't appear here again. Continue?`,
      acceptLabel: 'Generate File',
      rejectLabel: 'Cancel',
      accept: () => {
        this.generating = true;
        this.nachaService.generateDist(ids, this.effectiveDate).subscribe({
          next: (resp: HttpResponse<Blob>) => {
            this.generating = false;
            this.downloadBlob(resp, `KBM_DIST_${this.year}-${this.pad2(this.month)}.ach`);
            this.messageService.add({
              severity: 'success',
              summary: 'Distribution File Generated',
              detail: `Downloaded BoH ePay file with ${ids.length} entries.`
            });
            this.loadDistPreview();
            this.loadBatches();
          },
          error: (err) => {
            this.generating = false;
            this.error = err?.error?.message || err?.message || 'Generation failed.';
            this.messageService.add({
              severity: 'error',
              summary: 'Generation Failed',
              detail: this.error || 'See console for details.'
            });
          }
        });
      }
    });
  }

  // -----------------------------------------------------------------------
  // BATCHES (history)
  // -----------------------------------------------------------------------

  loadBatches() {
    this.nachaService.listBatches().subscribe({
      next: (rows: any[]) => { this.taxBatches = rows || []; },
      error: () => { /* non-fatal */ }
    });
    this.nachaService.listDistBatches().subscribe({
      next: (rows: any[]) => { this.distBatches = rows || []; },
      error: () => { /* non-fatal */ }
    });
  }

  markTaxBatchSubmitted(batch: any) {
    if (!batch?.nachaBatchId) return;
    this.confirmationService.confirm({
      header: 'Mark Batch Submitted?',
      message: `Mark batch #${batch.nachaBatchId} (${batch.taxType}) as SUBMITTED to the State of Hawaii? Do this AFTER you've uploaded the file.`,
      acceptLabel: 'Mark Submitted',
      rejectLabel: 'Cancel',
      accept: () => {
        this.nachaService.markSubmitted(batch.nachaBatchId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Batch Submitted',
              detail: `Batch #${batch.nachaBatchId} marked SUBMITTED.`
            });
            this.loadBatches();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Failed',
              detail: err?.error?.message || 'See console.'
            });
          }
        });
      }
    });
  }

  markDistBatchSubmitted(batch: any) {
    if (!batch?.nachaDistBatchId) return;
    this.confirmationService.confirm({
      header: 'Mark Distribution Batch Submitted?',
      message: `Mark batch #${batch.nachaDistBatchId} as SUBMITTED to Bank of Hawaii? Do this AFTER you've uploaded the file to BoH ePay.`,
      acceptLabel: 'Mark Submitted',
      rejectLabel: 'Cancel',
      accept: () => {
        this.nachaService.markDistSubmitted(batch.nachaDistBatchId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Batch Submitted',
              detail: `Distribution batch #${batch.nachaDistBatchId} marked SUBMITTED.`
            });
            this.loadBatches();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Failed',
              detail: err?.error?.message || 'See console.'
            });
          }
        });
      }
    });
  }

  /**
   * Inline delete from the main distribution list — invoked by the X button
   * next to the "Batch #N" pill on a property row. Frees the property to be
   * re-selected for a new batch. Refused server-side if the batch is
   * SUBMITTED.
   */
  deleteDistBatchInline(row: any) {
    if (!row?.nachaDistBatchId) return;
    this.confirmDeleteDistBatch(row.nachaDistBatchId,
      `Delete batch #${row.nachaDistBatchId}? This unlinks ${row.propertyCode} ` +
      `(and any other properties in the same batch) so they can be re-selected ` +
      `for a new wire. The batch row is hard-deleted. Refused if the batch was ` +
      `already marked SUBMITTED to BoH.`);
  }

  /**
   * Delete from the batch-history table at the bottom of the dialog.
   */
  deleteDistBatch(batch: any) {
    if (!batch?.nachaDistBatchId) return;
    if (batch.status === 'SUBMITTED') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Delete',
        detail: 'Batch is SUBMITTED to BoH. Reverse via the bank, do not delete locally.'
      });
      return;
    }
    this.confirmDeleteDistBatch(batch.nachaDistBatchId,
      `Delete batch #${batch.nachaDistBatchId}? This unlinks all ${batch.entryCount} ` +
      `statements in the batch so they can be re-selected. The batch row is hard-deleted.`);
  }

  private confirmDeleteDistBatch(nachaDistBatchId: number, message: string) {
    this.confirmationService.confirm({
      header: `Delete Distribution Batch #${nachaDistBatchId}?`,
      message,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.nachaService.deleteDistBatch(nachaDistBatchId).subscribe({
          next: (res: any) => {
            const n = res?.statementsUnlinked ?? 0;
            this.messageService.add({
              severity: 'success',
              summary: 'Batch Deleted',
              detail: `Batch #${nachaDistBatchId} deleted. ${n} statement(s) unlinked and ` +
                      `available to re-batch.`
            });
            // Reload BOTH the preview rows (so the "Batch #N" pill disappears
            // and the checkbox becomes selectable again) AND the batch history.
            this.loadDistPreview();
            this.loadBatches();
          },
          error: (err) => {
            // Server returns 409 with a plain string body for the SUBMITTED guard
            const msg = (typeof err?.error === 'string' ? err.error : null)
                     || err?.error?.message
                     || 'Delete failed. See console.';
            this.messageService.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: msg
            });
          }
        });
      }
    });
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  private downloadBlob(resp: HttpResponse<Blob>, defaultName: string) {
    const blob = resp.body;
    if (!blob) return;
    // Try to extract filename from Content-Disposition; fall back to default.
    let fileName = defaultName;
    const cd = resp.headers.get('Content-Disposition');
    if (cd) {
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) fileName = m[1];
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private toYYMMDD(d: Date): string {
    const yy = String(d.getFullYear() % 100).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  }

  private pad2(n: number): string { return String(n).padStart(2, '0'); }

  formatMoney(v: number | null | undefined): string {
    if (v == null) return '$0.00';
    return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  formatDate(d: any): string {
    if (!d) return '';
    try {
      const parsed = new Date(typeof d === 'string' ? d.replace(' ', 'T') : d);
      if (isNaN(parsed.getTime())) return String(d);
      return parsed.toLocaleString();
    } catch { return String(d); }
  }
}
